<template lang="pug">
.flex.flex-col(v-for="template in relations", :key="template.uuid")
  h3.mb-4 {{ template.name }}
  .flex.justify-end.mb-4
    QRInput.max-w-96(v-model="search[template.uuid]")
  TwinRelationTable(
    :target="props.twin",
    :source-template="template",
    :direction="template.relationDirection",
    :editable="false",
    :search="search[template.uuid]"
  )

.flex.flex-col(
  v-for="propRelation in filteredPropertyRelations",
  :key="propRelation.template.uuid"
)
  h3.mb-4 {{ propRelation.template.name }}
  .flex.justify-end.mb-4
    QRInput.max-w-96(v-model="search[propRelation.property.uuid]")
  TwinRelationTable(
    :target="props.twin",
    :search="search[propRelation.property.uuid]",
    :source-template="propRelation.template",
    :direction="'to'",
    :editable="false"
  )
</template>

<script setup lang="ts">
import { useTwinStore } from "../../../stores/twinManagement";
import { computed, onMounted, ref, watch } from "vue";
import { type TwinResponse } from "../../../services/twinManagement";
import TwinRelationTable from "./TwinRelationTable.vue";
import QRInput from "@shared/components/elements/QRInput.vue";

const props = defineProps<{
  twin: TwinResponse;
  hideReferenceTypes?: string[];
  includeReferenceTypes?: string[];
}>();

const twinStore = useTwinStore();

const templateRelations = computed(
  () => twinStore.templateRelationsLoadingState.result,
);

const relations = computed(() => {
  const ids = new Set();
  return [
    ...(templateRelations.value?.relationsTo ?? []).map((item) => ({
      ...item,
      relationDirection: "to",
    })),
    ...(templateRelations.value?.relationsFrom ?? []).map((item) => ({
      ...item,
      relationDirection: "from",
    })),
    // Relations might be to AND from, we only want to show them once, so we have to filter for uniqueness
  ]
    .filter((item) => {
      if (!ids.has(item.uuid)) {
        ids.add(item.uuid);
        return true;
      }
      return false;
    })
    .filter((item) => !props.hideReferenceTypes?.includes(item.type))
    .filter(
      (item) =>
        !props.includeReferenceTypes ||
        props.includeReferenceTypes.some((prefix) =>
          item.type.startsWith(prefix),
        ),
    );
});

const propertyRelations = computed(() => {
  // @ts-ignore: backend differs from specification
  return [...(templateRelations.value?.propertyRelations ?? [])]
    .filter(
      (propRelation) =>
        !props.hideReferenceTypes?.includes(propRelation.template.type),
    )
    .filter(
      (propRelation) =>
        !props.includeReferenceTypes ||
        props.includeReferenceTypes.some((prefix) =>
          propRelation.template.type.startsWith(prefix),
        ),
    );
});

const filteredPropertyRelations = computed(() => {
  const relationUuids = new Set(relations.value.map((r) => r.uuid));
  return propertyRelations.value.filter(
    (propRelation) => !relationUuids.has(propRelation.template.uuid),
  );
});

async function initData() {
  await twinStore.loadTemplateRelations(props.twin.uuid);
}

onMounted(() => {
  initData();
});

watch(
  () => props.twin,
  () => void initData(),
);

// Search
const search = ref<Record<string, string>>({});
</script>
